import React, { FunctionComponent, ChangeEvent } from 'react';
import Svg from '@justgiving/svg';
import { imageIcon } from '@justgiving/icons';
import { useTranslation } from 'react-i18next';
import styles from './ImageUpload.scss';
import IconButton from '../IconButton/IconButton';

interface Props {
  className?: string;
  iconClassName?: string;
  onImageUploaded: (imageFile: File) => void;
  onValidated?: (error: string) => void;
}

const MAX_FILE_SIZE_MB = 15;

const ImageUpload: FunctionComponent<Props> = ({
  className = '',
  iconClassName = '',
  onImageUploaded,
  onValidated,
}) => {
  const inputRef = React.createRef<HTMLInputElement>();
  const { t } = useTranslation('common', { useSuspense: false });

  const openUploadDialog = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const isValid = (file: File): boolean => {
    let message = '';
    if (!file.type.startsWith('image/')) {
      message = t('timeline.fileTypeError');
    } else if (file.size > MAX_FILE_SIZE_MB * 1000000) {
      message = t('timeline.fileSizeError', {
        fileSize: `${MAX_FILE_SIZE_MB}MB`,
      });
    }

    const valid = message === '';

    if (onValidated) {
      onValidated(message);
    }

    return valid;
  };

  const handleImageSelected = (event: ChangeEvent<HTMLInputElement>) => {
    if (!inputRef.current) {
      return;
    }

    const inputFiles = event.target.files;
    if (inputFiles) {
      for (let i = 0; i < inputFiles.length; i += 1) {
        const file = inputFiles.item(i);
        if (file && isValid(file)) {
          onImageUploaded(file);
        }
      }
    }

    inputRef.current.value = '';
  };

  return (
    <div className={`${styles.imageUpload} ${className}`}>
      <input
        ref={inputRef}
        aria-label={t('uploadImage.inputLabel')}
        type="file"
        accept="image/*"
        hidden
        onChange={handleImageSelected}
      />
      <IconButton
        onClick={openUploadDialog}
        aria-label={t('uploadImage.submitLabel')}
      >
        <Svg markup={imageIcon} className={`${styles.icon} ${iconClassName}`} />
      </IconButton>
    </div>
  );
};

export default ImageUpload;
